/**
 * The range every card and every graph on the Overview answers for.
 *
 * One range for the page, held in one place. The alternative is each panel
 * holding its own, and then a reader comparing two of them is comparing two
 * periods without being told.
 *
 * The presets are the periods somebody actually asks about. A custom range is
 * offered too, because "the Tuesday the waves ran" is a real question and no
 * list of presets contains it.
 */

import { asInstant, searchQuery } from './searchQuery'

export interface OverviewRange {
  /** A preset key, or 'custom' when the two dates below are in charge. */
  preset: string
  from: string
  to: string
}

export interface Preset {
  key: string
  label: string
  seconds: number
}

const HOUR = 3600
const DAY = 24 * HOUR

export const PRESETS: Preset[] = [
  { key: '24h', label: 'Last 24 hours', seconds: DAY },
  { key: '7d', label: 'Last 7 days', seconds: 7 * DAY },
  { key: '30d', label: 'Last 30 days', seconds: 30 * DAY },
  { key: '92d', label: 'Last 92 days', seconds: 92 * DAY },
]

export const DEFAULT_RANGE: OverviewRange = { preset: '24h', from: '', to: '' }

/**
 * The range as the service is asked for it.
 *
 * A preset is resolved against now at the moment of asking rather than being
 * stored as two instants, so a page left open over lunch still means the last
 * 24 hours when it next refreshes, not the 24 hours before lunch.
 */
export function toQuery(range: OverviewRange, now: Date = new Date()): string {
  if (range.preset !== 'custom') {
    const preset = PRESETS.find((p) => p.key === range.preset) ?? PRESETS[0]
    const since = new Date(now.getTime() - preset.seconds * 1000)
    return searchQuery({ from: since.toISOString(), to: now.toISOString() })
  }
  // A custom range carries the operator's own offset, for the reason in
  // searchQuery.ts: a wall clock with no zone is a question about whose clock.
  return searchQuery({ from: asInstant(range.from), to: asInstant(range.to) })
}

/**
 * Why this range cannot be asked for, or null when it can.
 *
 * `maxDays` comes from the service, in the previous answer, rather than being
 * a second copy of the bound here. Until one has arrived the console does not
 * refuse anything: guessing the bound and being wrong would refuse a range the
 * service would have answered, which is worse than a 400 the reader can see.
 */
export function whyRefused(
  range: OverviewRange,
  maxDays: number | null,
): string | null {
  if (range.preset !== 'custom') return null
  if (!range.from || !range.to) return 'Pick both ends of the range.'
  const from = new Date(range.from)
  const to = new Date(range.to)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return 'One of those dates cannot be read.'
  }
  if (from >= to) return 'The range ends before it starts.'
  if (maxDays !== null && to.getTime() - from.getTime() > maxDays * DAY * 1000) {
    return `The widest range this page answers for is ${maxDays} days.`
  }
  return null
}

/**
 * What the range is called, for the caption under a card: "In the last 7 days".
 *
 * It carries its own article. The first version returned "last 7 days" for a
 * preset and "the selected range" for a period, the caption added "the", and
 * a zoom rendered "In the the selected range".
 */
export function rangeLabel(range: OverviewRange): string {
  if (range.preset !== 'custom') {
    const preset = PRESETS.find((p) => p.key === range.preset) ?? PRESETS[0]
    return `the ${preset.label.toLowerCase()}`
  }
  return 'the selected range'
}

/**
 * An instant as the wall clock a date picker holds, in the browser's zone.
 *
 * The inverse of `asInstant` in searchQuery.ts, which reads a picker's wall
 * clock back in the same zone, so a zoom from the chart lands in the picker as
 * the hour it came from and goes back to the service as the same instant.
 */
export function toLocalInput(instant: string): string {
  const when = new Date(instant)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}T${pad(when.getHours())}:${pad(when.getMinutes())}`
}

/** The range a zoom produces: a period, held as the picker holds one. */
export function zoomedTo(from: string, to: string): OverviewRange {
  return { preset: 'custom', from: toLocalInput(from), to: toLocalInput(to) }
}
